import type { VerdictResponse, VerdictDecision } from '../data/types';

/**
 * VerdictHero — вердикт сделки, центральный элемент карточки компании.
 * Аудитория: юрист/бухгалтер/собственник — операционное решение «работать/осторожно/отказ»
 * + обоснование (pros/cons/стоп-факторы) + рекомендация по предоплате/кредиту.
 * Весь вердикт — FREE (вывод-тизер: захват SEO + демонстрация ценности).
 */

const DECISION_META: Record<
  VerdictDecision,
  { fallbackLabel: string; color: string; bg: string; icon: string }
> = {
  work: { fallbackLabel: 'Можно работать', color: 'var(--decision-work)', bg: 'var(--decision-work-bg)', icon: '✓' },
  caution: { fallbackLabel: 'Работать осторожно', color: 'var(--decision-caution)', bg: 'var(--decision-caution-bg)', icon: '⚠' },
  refuse: { fallbackLabel: 'Рекомендуем отказаться', color: 'var(--decision-refuse)', bg: 'var(--decision-refuse-bg)', icon: '✕' },
};

const CONFIDENCE_LABEL: Record<string, string> = { low: 'низкая', medium: 'средняя', high: 'высокая' };

// Значения из verdict-движка (DealRecommendation): prepayment safe|partial|avoid,
// credit ok|limited|none.
const PREPAYMENT_LABEL: Record<string, string> = {
  safe: 'постоплата возможна',
  partial: 'частичная предоплата',
  avoid: 'только 100% предоплата',
};

const CREDIT_LABEL: Record<string, string> = {
  ok: 'обычный лимит',
  limited: 'минимальный (с обеспечением)',
  none: 'не предоставлять',
};

function RecChip({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}

export function VerdictHero({ verdict }: { verdict: VerdictResponse }) {
  const meta = DECISION_META[verdict.decision] ?? DECISION_META.caution;
  const rec = verdict.recommendation;
  const hasStop = Array.isArray(verdict.stop_factors) && verdict.stop_factors.length > 0;
  const hasPros = Array.isArray(verdict.pros) && verdict.pros.length > 0;
  const hasCons = Array.isArray(verdict.cons) && verdict.cons.length > 0;
  const hasForeign = Array.isArray(verdict.foreign_sanctions) && verdict.foreign_sanctions.length > 0;

  if (verdict.risk_not_calculated) {
    return (
      <section
        aria-label="Вердикт сделки"
        style={{
          background: 'var(--surface-quiet)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg, 18px)',
          padding: 20,
          marginTop: 20,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--muted)' }}>
          Недостаточно данных для вердикта
        </div>
        <p style={{ fontSize: 14, color: 'var(--muted)', margin: '6px 0 0' }}>
          Риск-оценка ещё не рассчитана — вердикт по сделке будет доступен после обработки данных.
        </p>
      </section>
    );
  }

  return (
    <section
      aria-label="Вердикт сделки"
      style={{
        background: meta.bg,
        border: `1px solid ${meta.color}`,
        borderRadius: 'var(--radius-lg, 18px)',
        padding: 'clamp(16px, 3vw, 24px)',
        marginTop: 20,
      }}
    >
      {/* Decision badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span
          aria-hidden
          style={{
            flexShrink: 0,
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: meta.color,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 26,
            fontWeight: 700,
          }}
        >
          {meta.icon}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 'clamp(20px, 4vw, 28px)', fontWeight: 700, color: meta.color, lineHeight: 1.15, overflowWrap: 'anywhere' }}>
            {verdict.label || meta.fallbackLabel}
          </div>
          {verdict.confidence && (
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
              Уверенность оценки: {CONFIDENCE_LABEL[verdict.confidence] ?? verdict.confidence}
            </div>
          )}
        </div>
      </div>

      {/* Headline */}
      {verdict.headline && (
        <p style={{ margin: '14px 0 0', fontSize: 'clamp(15px, 2.5vw, 17px)', lineHeight: 1.5, color: 'var(--text)', overflowWrap: 'anywhere' }}>
          {verdict.headline}
        </p>
      )}

      {/* Стоп-факторы — всегда видны (честность, не прятать) */}
      {hasStop && (
        <div
          role="alert"
          style={{
            marginTop: 16,
            background: 'var(--decision-refuse-bg)',
            border: '1px solid var(--decision-refuse)',
            borderRadius: 'var(--radius-md, 12px)',
            padding: '12px 14px',
          }}
        >
          <strong style={{ color: 'var(--decision-refuse)', fontSize: 14 }}>⛔ Стоп-факторы</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
            {verdict.stop_factors.map((f, i) => (
              <li key={i} style={{ fontSize: 14, color: 'var(--text)', overflowWrap: 'anywhere' }}>{f}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Pros / Cons */}
      {(hasPros || hasCons) && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
            gap: 16,
            marginTop: 16,
          }}
        >
          {hasPros && (
            <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-md, 12px)', padding: '12px 14px', minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ok-500)', marginBottom: 6 }}>Аргументы «за»</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {verdict.pros.map((p, i) => (
                  <li key={i} style={{ fontSize: 14, lineHeight: 1.5, overflowWrap: 'anywhere' }}>{p}</li>
                ))}
              </ul>
            </div>
          )}
          {hasCons && (
            <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-md, 12px)', padding: '12px 14px', minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--risk-500)', marginBottom: 6 }}>Аргументы «против»</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {verdict.cons.map((c, i) => (
                  <li key={i} style={{ fontSize: 14, lineHeight: 1.5, overflowWrap: 'anywhere' }}>{c}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Иностранные санкции — ВНЕШНИЙ фактор (нейтральный info-блок, НЕ красный стоп) */}
      {hasForeign && (
        <div
          style={{
            marginTop: 16,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderLeft: '3px solid var(--navy-500)',
            borderRadius: 'var(--radius-md, 12px)',
            padding: '12px 14px',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-700)' }}>
            🌐 Иностранные санкции (внешний фактор)
          </div>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {verdict.foreign_sanctions.map((s, i) => (
              <li key={i} style={{ fontSize: 14, lineHeight: 1.5, overflowWrap: 'anywhere' }}>{s}</li>
            ))}
          </ul>
          <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>
            Санкции третьих стран. На работу с контрагентом внутри РФ не влияют — учитывайте
            при внешнеэкономической деятельности и международных расчётах.
          </p>
        </div>
      )}

      {/* Рекомендация по сделке */}
      {rec && (rec.summary || rec.prepayment || rec.credit) && (
        <div
          style={{
            marginTop: 16,
            background: 'var(--surface)',
            borderRadius: 'var(--radius-md, 12px)',
            padding: '14px 16px',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Рекомендация по сделке</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
            {rec.prepayment && <RecChip label="Предоплата" value={PREPAYMENT_LABEL[rec.prepayment] ?? rec.prepayment} />}
            {rec.credit && <RecChip label="Кредитный лимит" value={CREDIT_LABEL[rec.credit] ?? rec.credit} />}
          </div>
          {rec.summary && (
            <p style={{ margin: '12px 0 0', fontSize: 14, lineHeight: 1.5, color: 'var(--text)', overflowWrap: 'anywhere' }}>{rec.summary}</p>
          )}
        </div>
      )}

      {/* Дисклеймер — юридическая защита */}
      <p style={{ margin: '16px 0 0', fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>
        Вердикт — автоматическая рекомендация сервиса на основе открытых данных. Не является юридическим
        заключением; окончательное решение принимает пользователь.
      </p>
    </section>
  );
}
